import { TimelineState } from "@xzdarcy/react-timeline-editor";
import React, { useEffect, useState, FC, useRef, useMemo, useCallback } from "react";
import { Stack, useTheme } from "@mui/material";
import { TimelineEffect } from "@xzdarcy/react-timeline-editor";
import { Howl } from "howler";
import { TimelineEngine } from "@xzdarcy/react-timeline-editor";
import lottie, { AnimationItem } from "lottie-web";
import {
  ChangeEditMode,
  GetAudioPlayMode,
  GetAutoScroll,
  GetFps,
  GetHandleAudio,
  GetInFrame,
  GetIsFetching,
  GetIsLoading,
  GetOrderedActionsList,
  GetOutFrame,
  GetPreviewIsPlaying,
  GetPreviewTime,
  GetSequenceDuration,
  GetSequenceIsPlaying,
  GetShowSequence,
  GetZoomState,
  SetPreviewIsPlaying,
  SetPreviewTime,
  SetSequenceIsPlaying,
  SetSequenceTime,
  SetShowSequence,
  SetZoomState,
} from "../../Redux/Reducers/redux-slice-timetool";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import PlayerControls from "./Player-controls";
import { CustomTimelineAction } from "./custom-timeline-action";
import { throttle } from "throttle-debounce";
import GlobalConst from "../../Core/Utils/Constants/globals";
import useEventListener from "../../Utils/Hooks/useEventListener";
import { SequenceMetadataSegment } from "../../Core/Api/Types/sequence-metadata-segment";
import LeftTimelineActions from "./Left-timeline-actions";
import { useTranslation } from 'react-i18next';

export const Rates = [0.2, 0.5, 1.0, 1.5, 2.0];

class LottieControl {
  cacheMap: Record<string, AnimationItem> = {};

  private _goToAndStop(item: AnimationItem, time: number) {
    if (!item.getDuration()) return;
    const duration = item.getDuration() * 1000;
    time = time * 1000;
    if (time > duration) time = time % duration;
    item.goToAndStop(time);
  }

  enter(data: { id: string; src: string; startTime: number; endTime: number; time: number }) {
    const { id, src, startTime, time } = data;
    let item: AnimationItem;
    if (this.cacheMap[id]) {
      item = this.cacheMap[id];
      item.show();
      this._goToAndStop(item, time);
    } else {
      const ground = document.getElementById("player-ground-1");
      item = lottie.loadAnimation({
        name: id,
        container: ground!,
        renderer: "svg",
        loop: true,
        autoplay: false,
        path: src,
        rendererSettings: {
          className: "lottie-ani",
        },
      });

      item.addEventListener("loaded_images", () => {
        this._goToAndStop(item, time - startTime);
      });
      this.cacheMap[id] = item;
    }
  }

  update(data: { id: string; src: string; startTime: number; endTime: number; time: number }) {
    const { id, startTime, endTime, time } = data;
    const item = this.cacheMap[id];
    if (!item) return;
    this._goToAndStop(item, time - startTime);
  }

  leave(data: { id: string; startTime: number; endTime: number; time: number }) {
    const { id, startTime, endTime, time } = data;
    const item = this.cacheMap[id];
    if (!item) return;
    if (time > endTime || time < startTime) {
      item.hide();
    } else {
      const cur = time - startTime;
      item.show();
      this._goToAndStop(item, cur);
    }
  }

  destroy() {
    lottie.destroy();
    this.cacheMap = {};
  }
}

let lottieControl = new LottieControl();

class AudioControl {
  cacheMap: Record<string, Howl> = {};
  listenerMap: Record<
    string,
    {
      time?: (data: { time: number }) => void;
      rate?: (data: { rate: number }) => void;
    }
  > = {};

  start(data: { id: string; engine: TimelineEngine; src: string; startTime: number; time: number }) {
    const { id, src, startTime, time, engine } = data;
    let item: Howl;
    if (this.cacheMap[id]) {
      item = this.cacheMap[id];
      item.rate(engine.getPlayRate());
      item.seek((time - startTime) % item.duration());
      item.play();
    } else {
      item = new Howl({ src, loop: true, autoplay: true });
      this.cacheMap[id] = item;
      item.on("load", () => {
        item.rate(engine.getPlayRate());
        item.seek((time - startTime) % item.duration());
      });
    }

    const timeListener = (data: { time: number }) => {
      const { time } = data;
      item.seek(time);
    };
    const rateListener = (data: { rate: number }) => {
      const { rate } = data;
      item.rate(rate);
    };
    if (!this.listenerMap[id]) this.listenerMap[id] = {};
    engine.on("afterSetTime", timeListener);
    engine.on("afterSetPlayRate", rateListener);
    this.listenerMap[id].time = timeListener;
    this.listenerMap[id].rate = rateListener;
  }

  stop(data: { id: string; engine: TimelineEngine }) {
    const { id, engine } = data;
    if (this.cacheMap[id]) {
      const item = this.cacheMap[id];
      item.stop();
      if (this.listenerMap[id]) {
        this.listenerMap[id].time && engine.off("afterSetTime", this.listenerMap[id].time);
        this.listenerMap[id].rate && engine.off("afterSetPlayRate", this.listenerMap[id].rate);
        delete this.listenerMap[id];
      }
    }
  }
}

let audioControl = new AudioControl();

export const mockEffect: Record<string, TimelineEffect> = {
  effect0: {
    id: "effect0",
    name: "effect0",
    source: {
      start: ({ action, engine, isPlaying, time }) => {
        if (isPlaying) {
          const src = (action as CustomTimelineAction).data.src;
          audioControl.start({ id: src, src, startTime: action.start, engine, time });
        }
      },
      enter: ({ action, engine, isPlaying, time }) => {
        if (isPlaying) {
          const src = (action as CustomTimelineAction).data.src;
          audioControl.start({ id: src, src, startTime: action.start, engine, time });
        }
      },
      leave: ({ action, engine }) => {
        const src = (action as CustomTimelineAction).data.src;
        audioControl.stop({ id: src, engine });
      },
      stop: ({ action, engine }) => {
        const src = (action as CustomTimelineAction).data.src;
        audioControl.stop({ id: src, engine });
      },
    },
  },
  effect1: {
    id: "effect1",
    name: "effect1",
    source: {
      enter: ({ action, time }) => {
        const src = (action as CustomTimelineAction).data.src;
        lottieControl.enter({ id: src, src, startTime: action.start, endTime: action.end, time });
      },
      update: ({ action, time }) => {
        const src = (action as CustomTimelineAction).data.src;
        lottieControl.update({ id: src, src, startTime: action.start, endTime: action.end, time });
      },
      leave: ({ action, time }) => {
        const src = (action as CustomTimelineAction).data.src;
        lottieControl.leave({ id: src, startTime: action.start, endTime: action.end, time });
      },
    },
  },
};

const TimelinePlayer: FC<{
  timelineState: React.MutableRefObject<TimelineState | null>;
  autoScrollWhenPlay: React.MutableRefObject<boolean>;
  timelineWidth: number;
  timelineScale: number;
  onGenerated: (segments: SequenceMetadataSegment[]) => void;
}> = ({ timelineState, autoScrollWhenPlay, timelineWidth, timelineScale, onGenerated }) => {
  const dispatch = useDispatch();
  const [time, setTime] = useState(0);
  const [zoomValue, setZoomValue] = useState(1);
  const [currentScroll, setCurrentScroll] = useState(0);
  const [triggerMemo, setTriggerMemo] = useState(false);
  const isPlaying = useSelector(GetSequenceIsPlaying);
  const previewIsPlaying = useSelector(GetPreviewIsPlaying);
  const showSequence = useSelector(GetShowSequence);
  const orderedActionsList = useSelector(GetOrderedActionsList);
  const duration = useSelector(GetSequenceDuration);
  const previewTime = useSelector(GetPreviewTime);
  const inFrame = useSelector(GetInFrame);
  const outFrame = useSelector(GetOutFrame);
  const fps = useSelector(GetFps);
  const zoomState = useSelector(GetZoomState);
  const isFetching = useSelector(GetIsFetching);
  const autoScroll = useSelector(GetAutoScroll);
  const zoomStateRef = useRef<{ scale: number; width: number }>(zoomState);
  const autoScrollRef = useRef<boolean>(autoScroll);
  const handleAudio = useSelector(GetHandleAudio);
  const audioPlayMode = useSelector(GetAudioPlayMode);
  const isLoading = useSelector(GetIsLoading);

  useEffect(() => {
    zoomStateRef.current = zoomState;
  }, [zoomState]);
  useEffect(() => {
    autoScrollRef.current = autoScroll;
  }, [autoScroll]);

  const theme = useTheme();

  const domRef = useRef<HTMLDivElement | null>(null);

  // compute new scroll position
  useEffect(() => {
    if (!autoScrollRef.current) return;
    setTimeout(() => {
      const timelineW = domRef.current?.offsetWidth ?? -1;
      const cursor = document.getElementsByClassName("timeline-editor-cursor");
      const cursorLeft = parseFloat(cursor[0]?.getAttribute("data-left") || "0");
      if (!cursorLeft || timelineW === -1) return;
      if (cursorLeft < (timelineW - 124) * 0.15 || cursorLeft > (timelineW - 124) * 0.82) {
        const left =
          (Math.round(time * 100) / 100) * (zoomStateRef.current.width / zoomStateRef.current.scale) -
          timelineW / 2 +
          170;
        timelineState.current?.setScrollLeft(left);
      }
    }, 0);
  }, [time]);

  const handleTimeUpdate = useCallback(({ time }) => {
    setTime(Math.round(time * 100) / 100);
    dispatch(SetSequenceIsPlaying(false));
    dispatch(SetPreviewIsPlaying(false));
    dispatch(SetShowSequence(true));
    setTimeout(() => {
      dispatch(SetSequenceTime(time));
    }, 0);
  }, []);

  const handleToggleSequenceIsPlaying = useCallback((nextIsPlayingState: boolean) => {
    dispatch(SetSequenceIsPlaying(nextIsPlayingState));
    if (timelineState.current) {
      const nextTimeState = timelineState.current.getTime();
      setTime(nextTimeState);
    }
  }, []);

  const handlePlayOrPause = useCallback(() => {
    if (showSequence) {
      if (timelineState.current && timelineState.current.isPlaying === isPlaying) {
        dispatch(SetSequenceIsPlaying(!isPlaying));
      }
    } else {
      dispatch(SetPreviewIsPlaying(!previewIsPlaying));
    }
  }, [isPlaying, previewIsPlaying, showSequence]);

  const handleSliderZoom = useCallback((e) => {
    const scaleFixedValues = [1, 2, 5, 10, 30, 60, 120, 300, 900, 1800, 7200];
    if (!timelineState.current) return;
    const sliderValue = e.target.value;
    setZoomValue(sliderValue);

    const minScale = 1;
    const maxScale = 3600;

    const cursorPos = timelineState.current?.getTime();
    if (cursorPos === undefined) return;

    const timelineW = (domRef.current?.offsetWidth ?? 0) - 300;

    if (timelineW <= 0) return;

    const minExp = Math.log(minScale);
    const maxExp = Math.log(maxScale);
    const scaleExp = minExp + (maxExp - minExp) * ((100 - sliderValue) / 100);
    const scaleE = Math.exp(scaleExp);

    let scale = scaleFixedValues[scaleFixedValues.findLastIndex((x) => x < scaleE)];

    const minPixelsPerSecond = 50;
    const maxPixelsPerSecond = 500;

    const pixelsPerSecond = parseInt(
      (maxPixelsPerSecond + (minPixelsPerSecond - maxPixelsPerSecond) * ((scaleE - scale) / scaleE)).toString()
    );

    dispatch(SetZoomState({ scale: scale, width: pixelsPerSecond }));

    const cursorPosInPx = (cursorPos / scale) * pixelsPerSecond;

    const newScrollPos = parseInt((cursorPosInPx - timelineW / 2).toString());
    timelineState.current?.setScrollLeft(newScrollPos);

    setCurrentScroll(newScrollPos);
  }, []);

  const throttleSliderZoom = useMemo(
    () =>
      throttle(Math.trunc(GlobalConst.SEARCH_THROTTLE_DELAY_MS / 2), (e) => handleSliderZoom(e), {
        noLeading: true,
      }),
    [handleSliderZoom]
  );

  const handleScroll = useCallback(
    (direction: "left" | "right") => {
      if (!timelineState.current) return;
      const scrollTo = direction === "left" ? -zoomState.width + currentScroll : zoomState.width + currentScroll;
      timelineState.current.setScrollLeft(scrollTo >= 0 ? scrollTo : 0);
      setCurrentScroll(scrollTo >= 0 ? scrollTo : 0);
    },
    [zoomState.width, currentScroll]
  );

  const handleShortcutKeyup = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case "Control":
        dispatch(ChangeEditMode("default"));
        break;
      default:
        break;
    }
  }, []);

  const handleGoTo = useCallback(
    (target: string) => {
      dispatch(SetSequenceIsPlaying(false));
      if (!timelineState.current) return;
      let currentSegmentIndex: number | undefined;
      if (showSequence) {
        switch (target) {
          case "start":
            timelineState.current.setTime(0);
            break;
          case "end":
            timelineState.current.setTime((duration - 1) / fps);
            break;
          case "previous_segment":
            currentSegmentIndex = orderedActionsList.findIndex((x) => x.start < time && x.end >= time) || 0;
            if (currentSegmentIndex > 0) {
              timelineState.current.setTime(orderedActionsList[currentSegmentIndex - 1].end);
            } else timelineState.current.setTime(0);
            break;
          case "next_segment":
            currentSegmentIndex = orderedActionsList.findIndex((x) => x.start <= time && x.end > time) || 0;
            if (currentSegmentIndex < orderedActionsList.length - 1) {
              timelineState.current.setTime(orderedActionsList[currentSegmentIndex + 1].start);
            } else timelineState.current.setTime((duration - 1) / fps);
            break;
          case "previous_frame":
            if (time >= 1 / fps) {
              timelineState.current.setTime(time - 1 / fps);
            }
            break;
          case "next_frame":
            if (time < duration - 1 / fps) {
              timelineState.current.setTime(time + 1 / fps);
            }
            break;
          case "previous_second":
            if (time >= 1) {
              timelineState.current.setTime(time - 1);
            }
            break;
          case "next_second":
            if (time < duration - 1) {
              timelineState.current.setTime(time + 1);
            }
            break;
          default:
            break;
        }
      } else {
        dispatch(SetPreviewIsPlaying(false));
        setTimeout(() => {
          switch (target) {
            case "start":
              inFrame ? dispatch(SetPreviewTime(inFrame / fps)) : dispatch(SetPreviewTime(0));
              break;
            case "end":
              outFrame
                ? dispatch(SetPreviewTime((outFrame - 1) / fps))
                : dispatch(SetPreviewTime((duration - 1) / fps));
              break;
            case "previous_segment":
              if (inFrame !== null && (previewTime - 10) * fps > inFrame) {
                dispatch(SetPreviewTime(previewTime - 10));
              } else {
                inFrame ? dispatch(SetPreviewTime(inFrame / fps)) : dispatch(SetPreviewTime(0));
              }
              break;
            case "next_segment":
              if (outFrame && (previewTime + 10) * fps < outFrame) {
                dispatch(SetPreviewTime(previewTime + 10));
              } else {
                outFrame ? dispatch(SetPreviewTime(outFrame / fps)) : dispatch(SetPreviewTime(duration - 1));
              }
              break;
            case "previous_frame":
              if (inFrame !== null && previewTime * fps - 1 > inFrame) {
                dispatch(SetPreviewTime(previewTime - 1 / fps));
              } else {
                inFrame ? dispatch(SetPreviewTime(inFrame / fps)) : dispatch(SetPreviewTime(0));
              }
              break;
            case "next_frame":
              if (outFrame && previewTime * fps + 1 < outFrame) {
                dispatch(SetPreviewTime(previewTime + 1 / fps));
              } else {
                outFrame ? dispatch(SetPreviewTime(outFrame / fps)) : dispatch(SetPreviewTime(duration - 1));
              }
              break;
            case "previous_second":
              if (inFrame !== null && (previewTime - 1) * fps > inFrame) {
                dispatch(SetPreviewTime(previewTime - 1));
              } else {
                inFrame ? dispatch(SetPreviewTime(inFrame / fps)) : dispatch(SetPreviewTime(0));
              }
              break;
            case "next_second":
              if (outFrame && (previewTime + 1) * fps < outFrame) {
                dispatch(SetPreviewTime(previewTime + 1));
              } else {
                outFrame ? dispatch(SetPreviewTime(outFrame / fps)) : dispatch(SetPreviewTime(duration - 1));
              }
              break;
            default:
              break;
          }
        }, 0);
      }
    },
    [inFrame, outFrame, time, previewTime, duration, showSequence, orderedActionsList]
  );

  const handleShortcut = useCallback(
    (e: KeyboardEvent) => {
      if ((e.target as any).localName && (e.target as any).localName === "input") return;
      switch (e.key) {
        case "Control":
          dispatch(ChangeEditMode("cut"));
          break;
        case ";":
          handleGoTo("previous_segment");
          break;
        case ":":
          handleGoTo("next_segment");
          break;
        case ",":
          handleGoTo("start");
          break;
        case "!":
          handleGoTo("end");
          break;
        case "k":
        case " ":
          !isFetching && handlePlayOrPause();
          break;
        default:
          break;
      }
    },
    [handleGoTo]
  );

  useEventListener("keydown", handleShortcut);
  useEventListener("keyup", handleShortcutKeyup);

  useEffect(() => {
    timelineState.current && dispatch(SetSequenceTime(Math.round(timelineState.current.getTime() * 100) / 100));
  }, [showSequence]);

  const setTimeByTickCallback = useCallback(({ time }) => {
    setTime(Math.round(time * 100) / 100);
  }, []);

  useEffect(() => {
    if (!timelineState.current) return;
    const engine = timelineState.current;
    engine.listener.on("play", () => handleToggleSequenceIsPlaying(true));
    engine.listener.on("paused", (e) => {
      handleToggleSequenceIsPlaying(false);
    });
    engine.listener.on("afterSetTime", handleTimeUpdate);
    engine.listener.on("setTimeByTick", setTimeByTickCallback);

    if (autoScrollWhenPlay.current) {
      const left = time * (timelineWidth / timelineScale) + -280;
      engine.setScrollLeft(left);
    }

    handleSliderZoom({ target: { value: 1 } });

    return () => {
      if (!engine) return;
      engine.pause();
      engine.listener.offAll();
      lottieControl.destroy();
    };
  }, []);

  useEffect(() => {
    if (timelineState.current && timelineState.current.isPlaying !== isPlaying) {
      if (isPlaying) {
        timelineState.current.play({ autoEnd: true });
      } else {
        timelineState.current.pause();
      }
    }
  }, [isPlaying]);

  const ControlsMemo = useMemo(
    () => (
      <PlayerControls
        handleGoTo={handleGoTo}
        handlePlayOrPause={handleToggleSequenceIsPlaying}
        isPlaying={showSequence ? isPlaying : previewIsPlaying}
      />
    ),
    [previewIsPlaying, triggerMemo, showSequence, isPlaying, handleGoTo, handlePlayOrPause]
  );

  return (
    <Stack
      ref={domRef}
      height="40px"
      width="100%"
      className="play-control"
      display="flex"
      direction="row"
      justifyContent="space-between"
      sx={{ opacity: 0.7, backgroundColor: theme.palette.background[200] }}
      position="sticky"
    >
      {!isLoading && (
        <LeftTimelineActions
          audioPlayMode={audioPlayMode}
          fps={fps}
          handleAudio={handleAudio}
          handleScroll={handleScroll}
          handleSliderZoom={handleSliderZoom}
          onGenerated={onGenerated}
          throttleSliderZoom={throttleSliderZoom}
          time={time}
          zoomValue={zoomValue}
        />
      )}
      {ControlsMemo}
    </Stack>
  );
};

export default TimelinePlayer;
